// LibraryContext.js — bibliothèque personnelle du membre : favoris + wishlist.
// Persistés dans le stockage cloud Nakama (repli localStorage pour l'invité local).
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { nakamaAuth } from '../services/nakamaAuth';
import { useAuth } from './AuthContext';

const COLL = 'library';
const LibraryContext = createContext(null);

export function LibraryProvider({ children }) {
  const { session } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [ready, setReady] = useState(false);

  // (Re)charge à chaque changement de session (connexion / invité / déconnexion).
  useEffect(() => {
    let alive = true;
    (async () => {
      const f = await nakamaAuth.storageRead(COLL, 'favorites', { ids: [] });
      const w = await nakamaAuth.storageRead(COLL, 'wishlist', { ids: [] });
      if (!alive) return;
      setFavorites(Array.isArray(f && f.ids) ? f.ids : []);
      setWishlist(Array.isArray(w && w.ids) ? w.ids : []);
      setReady(true);
    })();
    return () => { alive = false; };
  }, [session]);

  const persist = useCallback((key, ids) => { nakamaAuth.storageWrite(COLL, key, { ids }); }, []);

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      persist('favorites', next);
      return next;
    });
  }, [persist]);

  const toggleWishlist = useCallback((id) => {
    setWishlist((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      persist('wishlist', next);
      return next;
    });
  }, [persist]);

  const isFavorite = useCallback((id) => favorites.includes(id), [favorites]);
  const inWishlist = useCallback((id) => wishlist.includes(id), [wishlist]);

  return (
    <LibraryContext.Provider value={{ favorites, wishlist, ready, toggleFavorite, toggleWishlist, isFavorite, inWishlist }}>
      {children}
    </LibraryContext.Provider>
  );
}

export function useLibrary() {
  return useContext(LibraryContext) || {
    favorites: [], wishlist: [], ready: false,
    toggleFavorite() {}, toggleWishlist() {}, isFavorite() { return false; }, inWishlist() { return false; },
  };
}
